import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded'
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded'
import DeselectRoundedIcon from '@mui/icons-material/DeselectRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import SelectAllRoundedIcon from '@mui/icons-material/SelectAllRounded'
import { FilterChip } from '../Common/Chip'
import { DatePicker, DateRangePicker } from '../Common/DatePicker'
import { Slider } from '../Common/Slider'
import { TextInput } from '../Common/TextInput'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import {
  isQuestionSetReady,
  type AiMagicQuestion,
  type AiMagicQuestionSet,
} from '../../services/ai/interview/aiMagicQuestionSet'
import type { InterviewAnswers } from './interviewSubmit'
import { useNls } from '../../nls/nlsProvider'

export interface InterviewQuestionSetProps {
  set: AiMagicQuestionSet
  answers: InterviewAnswers
  onAnswersChange: (answers: InterviewAnswers) => void
  /** id of the question that the composer treats as focused. Kept for parity
   *  with the panel's per-mode state, but in carousel mode we drive it from
   *  the internal `currentIndex`. */
  focusedQuestionId: string | null
  onFocusQuestion: (id: string) => void
  /** Called when the user hits the ↵ submit control on the last question. */
  onSubmit: () => void
  /** No longer surfaced in the UI (Skip button removed per design pivot) —
   *  the panel still owns the underlying "discard set" side effect and can
   *  invoke it from elsewhere if needed. */
  onSkip: () => void
  disabled?: boolean
  /** True while the composer's textarea has free-text content: the interview
   *  becomes read-only (visually greyed, no interactions) so the user can
   *  clearly see the escape hatch is active. Empty textarea → back to normal. */
  blocked?: boolean
}

/* -------------------------------------------------------------------------- */
/*  Answer helpers                                                            */
/* -------------------------------------------------------------------------- */

const singleValue = (value: InterviewAnswers[string]): string => {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value[0] ?? ''
  return ''
}

const listValue = (value: InterviewAnswers[string]): string[] => {
  if (Array.isArray(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) return [value]
  return []
}

/* -------------------------------------------------------------------------- */
/*  Per-type controls (compact carousel variants)                             */
/* -------------------------------------------------------------------------- */

interface ControlProps {
  question: AiMagicQuestion
  answer: InterviewAnswers[string]
  onChange: (next: InterviewAnswers[string]) => void
  disabled?: boolean
}

const RadioControl = ({ question, answer, onChange, disabled }: ControlProps) => {
  const current = singleValue(answer)
  return (
    <div className="interviewChipRow" role="radiogroup" aria-label={question.prompt}>
      {question.options?.map((option) => {
        const selected = current === option.value
        return (
          <FilterChip
            key={option.value}
            selectionRole="radio"
            selected={selected}
            disabled={disabled}
            className={`interviewChip ${selected ? 'isSelected' : ''}`}
            onClick={() => onChange(option.value)}
          >
            <span className="interviewChipDot" aria-hidden="true" />
            <span>{option.label}</span>
          </FilterChip>
        )
      })}
    </div>
  )
}

const CheckboxControl = ({ question, answer, onChange, disabled }: ControlProps) => {
  const current = listValue(answer)
  const toggle = (value: string) => {
    onChange(current.includes(value)
      ? current.filter((entry) => entry !== value)
      : [...current, value])
  }
  return (
    <div className="interviewChipRow" role="group" aria-label={question.prompt}>
      {question.options?.map((option) => {
        const selected = current.includes(option.value)
        return (
          <FilterChip
            key={option.value}
            selectionRole="checkbox"
            selected={selected}
            disabled={disabled}
            className={`interviewChip isCheckbox ${selected ? 'isSelected' : ''}`}
            onClick={() => toggle(option.value)}
          >
            <span className="interviewChipTick" aria-hidden="true" />
            <span>{option.label}</span>
          </FilterChip>
        )
      })}
    </div>
  )
}

const TextControl = ({ question, answer, onChange, disabled }: ControlProps) => (
  <TextInput
    value={singleValue(answer)}
    placeholder={question.placeholder ?? 'Type your answer…'}
    disabled={disabled}
    autoFocus
    aria-label={question.prompt}
    onChange={onChange}
  />
)

const DateControl = ({ question, answer, onChange, disabled }: ControlProps) => (
  <DatePicker
    value={singleValue(answer)}
    onChange={onChange}
    disabled={disabled}
    ariaLabel={question.prompt}
  />
)

const DateRangeControl = ({ question, answer, onChange, disabled }: ControlProps) => {
  const current = singleValue(answer)
  const [from, to] = current.split('|')
  const commit = (nextFrom: string, nextTo: string) => {
    if (!nextFrom && !nextTo) onChange('')
    else onChange(`${nextFrom}|${nextTo}`)
  }
  return (
    <DateRangePicker
      startValue={from}
      endValue={to}
      disabled={disabled}
      ariaLabel={question.prompt}
      onChange={({ start, end }) => commit(start, end)}
    />
  )
}

const NumberControl = ({ question, answer, onChange, disabled }: ControlProps) => (
  <TextInput
    type="number"
    value={singleValue(answer)}
    placeholder={question.placeholder}
    min={question.min}
    max={question.max}
    step={question.step ?? 1}
    disabled={disabled}
    autoFocus
    aria-label={question.prompt}
    onChange={onChange}
  />
)

const SliderControl = ({ question, answer, onChange, disabled }: ControlProps) => {
  const min = question.min ?? 0
  const max = question.max ?? 100
  const step = question.step ?? 1
  const current = singleValue(answer)
  const numeric = Number.parseFloat(current)
  const value = Number.isFinite(numeric) ? numeric : Math.round((min + max) / 2)
  return (
    <div className="interviewSliderRow">
      <Slider
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        value={value}
        onChange={(next) => onChange(String(next))}
        aria-label={question.prompt}
      />
      <span className="interviewSliderReadout" aria-live="polite">{value}</span>
    </div>
  )
}

const AttachmentInventoryControl = ({ question, answer, onChange, disabled }: ControlProps) => {
  const { t } = useNls()
  const inventory = question.attachmentInventory
  const selected = new Set(listValue(answer))
  const [collapsedSources, setCollapsedSources] = useState<Set<string>>(new Set())
  const [showAll, setShowAll] = useState(false)
  if (!inventory) return null

  const allItems = inventory.sources.flatMap((source) => source.items)
  const visibleIds = new Set((showAll ? allItems : allItems.slice(0, 40)).map((item) => item.id))
  const setSelected = (ids: string[]) => onChange(Array.from(new Set(ids)))
  const toggleItem = (id: string) => setSelected(selected.has(id)
    ? Array.from(selected).filter((value) => value !== id)
    : [...selected, id])
  const selectSource = (ids: string[]) => setSelected([...selected, ...ids])
  const deselectSource = (ids: string[]) => setSelected(Array.from(selected).filter((id) => !ids.includes(id)))

  return (
    <section className="attachmentInventory" aria-label={t('NLSAiInterviewAttachmentIntro', { count: inventory.extractedCount })}>
      <div className="attachmentInventory__globalActions">
        <Button
          size="sm"
          variant="tertiary"
          disabled={disabled}
          onClick={() => setSelected(allItems.map((item) => item.id))}
          icon={<SelectAllRoundedIcon aria-hidden="true" />}
        >
          {t('NLSAiInterviewAttachmentSelectAll')}
        </Button>
        <Button
          size="sm"
          variant="tertiary"
          disabled={disabled}
          onClick={() => setSelected([])}
          icon={<DeselectRoundedIcon aria-hidden="true" />}
        >
          {t('NLSAiInterviewAttachmentSelectNone')}
        </Button>
        <Button
          size="sm"
          variant="tertiary"
          disabled={disabled}
          onClick={() => setSelected(allItems.slice(0, 20).map((item) => item.id))}
          icon={<SelectAllRoundedIcon aria-hidden="true" />}
        >
          {t('NLSAiInterviewAttachmentKeepTop20')}
        </Button>
      </div>
      <div className="attachmentInventory__sources">
        {inventory.sources.map((source) => {
          const sourceItems = source.items.filter((item) => visibleIds.has(item.id))
          if (sourceItems.length === 0) return null
          const collapsed = collapsedSources.has(source.sourceId)
          const sourceIds = source.items.map((item) => item.id)
          return (
            <section className="attachmentInventory__source" key={source.sourceId}>
              <div className="attachmentInventory__sourceHeader">
                <Pressable
                  className="attachmentInventory__sourceToggle"
                  aria-expanded={!collapsed}
                  disabled={disabled}
                  onClick={() => setCollapsedSources((current) => {
                    const next = new Set(current)
                    if (next.has(source.sourceId)) next.delete(source.sourceId)
                    else next.add(source.sourceId)
                    return next
                  })}
                >
                  {collapsed ? <ExpandMoreRoundedIcon aria-hidden="true" /> : <ExpandLessRoundedIcon aria-hidden="true" />}
                  {source.source} <span>{source.items.length}</span>
                </Pressable>
                <span className="attachmentInventory__sourceActions">
                  <Button
                    size="sm"
                    variant="tertiary"
                    disabled={disabled}
                    onClick={() => selectSource(sourceIds)}
                    title={t('NLSAiInterviewAttachmentSelectFile', { source: source.source })}
                    icon={<SelectAllRoundedIcon aria-hidden="true" />}
                  >
                    {t('NLSAiInterviewAttachmentSelectFile', { source: source.source })}
                  </Button>
                  <Button
                    size="sm"
                    variant="tertiary"
                    disabled={disabled}
                    onClick={() => deselectSource(sourceIds)}
                    title={t('NLSAiInterviewAttachmentSelectNoneFile', { source: source.source })}
                    icon={<DeselectRoundedIcon aria-hidden="true" />}
                  >
                    {t('NLSAiInterviewAttachmentSelectNoneFile', { source: source.source })}
                  </Button>
                </span>
              </div>
              {!collapsed && (
                <div className="attachmentInventory__items" role="group" aria-label={source.source}>
                  {sourceItems.map((item) => {
                    const checked = selected.has(item.id)
                    return (
                      <Pressable
                        key={item.id}
                        role="checkbox"
                        aria-checked={checked}
                        disabled={disabled}
                        className={`attachmentInventory__item${checked ? ' isSelected' : ''}`}
                        title={item.excerpt}
                        onClick={() => toggleItem(item.id)}
                      >
                        {checked ? <CheckBoxRoundedIcon aria-hidden="true" /> : <CheckBoxOutlineBlankRoundedIcon aria-hidden="true" />}
                        <span className="attachmentInventory__itemLabel">{item.label}</span>
                        <span className="attachmentInventory__kind">{item.kind}</span>
                      </Pressable>
                    )
                  })}
                </div>
              )}
            </section>
          )
        })}
      </div>
      {inventory.extractedCount > 40 && !showAll ? (
        <Button
          size="sm"
          variant="tertiary"
          className="attachmentInventory__showAll"
          disabled={disabled}
          onClick={() => setShowAll(true)}
          icon={<ExpandMoreRoundedIcon aria-hidden="true" />}
        >
          {t('NLSAiInterviewAttachmentShowAll', { count: inventory.extractedCount })}
        </Button>
      ) : null}
    </section>
  )
}

const renderControl = (props: ControlProps) => {
  switch (props.question.type) {
    case 'RADIO':      return <RadioControl {...props} />
    case 'CHECKBOX':   return <CheckboxControl {...props} />
    case 'DATE':       return <DateControl {...props} />
    case 'DATE_RANGE': return <DateRangeControl {...props} />
    case 'NUMBER':     return <NumberControl {...props} />
    case 'SLIDER':     return <SliderControl {...props} />
    case 'ATTACHMENT_INVENTORY': return <AttachmentInventoryControl {...props} />
    case 'TEXT':
    default:           return <TextControl {...props} />
  }
}

/* -------------------------------------------------------------------------- */
/*  Carousel                                                                  */
/* -------------------------------------------------------------------------- */

const ChevronLeftGlyph = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <path d="M10.5 3 L5.5 8 L10.5 13" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
const ChevronRightGlyph = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <path d="M5.5 3 L10.5 8 L5.5 13" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
const EnterGlyph = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <path d="M3 9.5 L11.5 9.5 L8.5 12.5 M11.5 9.5 L8.5 6.5 M11.5 9.5 L13 9.5 L13 4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const InterviewQuestionSet = ({
  set,
  answers,
  onAnswersChange,
  focusedQuestionId,
  onFocusQuestion,
  onSubmit,
  disabled,
  blocked,
}: InterviewQuestionSetProps) => {
  const { t } = useNls()
  const total = set.questions.length
  // Position the carousel: prefer the panel-provided focus id, else start at 0.
  const initialIndex = useMemo(() => {
    if (!focusedQuestionId) return 0
    const idx = set.questions.findIndex((q) => q.id === focusedQuestionId)
    return idx >= 0 ? idx : 0
  }, [focusedQuestionId, set.questions])
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex)
  // 'left' → slide the outgoing card left, 'right' → slide it right.
  const [slideDir, setSlideDir] = useState<'left' | 'right' | 'none'>('none')
  // Guard against the auto-advance timer firing after the set unmounts.
  const advanceTimerRef = useRef<number | null>(null)

  // Hydrate defaults exactly once per set identity (not per question).
  useEffect(() => {
    const seeded: InterviewAnswers = { ...answers }
    let touched = false
    for (const q of set.questions) {
      if (seeded[q.id] !== undefined) continue
      if (q.default !== undefined) {
        seeded[q.id] = q.default
        touched = true
      }
    }
    if (touched) onAnswersChange(seeded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set.id])

  // Clamp currentIndex to the set bounds whenever the set changes.
  useEffect(() => {
    if (currentIndex >= total) setCurrentIndex(Math.max(0, total - 1))
  }, [total, currentIndex])

  // Keep the panel's focus id in lockstep with the visible question.
  useEffect(() => {
    const q = set.questions[currentIndex]
    if (q && q.id !== focusedQuestionId) onFocusQuestion(q.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, set.id])

  // Clear pending auto-advance on unmount.
  useEffect(() => () => {
    if (advanceTimerRef.current != null) window.clearTimeout(advanceTimerRef.current)
  }, [])

  const currentQuestion = set.questions[currentIndex] ?? set.questions[0]
  const answer = answers[currentQuestion.id]
  const isFirst = currentIndex <= 0
  const isLast = currentIndex >= total - 1
  const behindCount = currentIndex
  const aheadCount = total - currentIndex - 1

  const goPrev = useCallback(() => {
    if (isFirst || disabled || blocked) return
    setSlideDir('right')
    setCurrentIndex((i) => Math.max(0, i - 1))
  }, [isFirst, disabled, blocked])

  // Right arrow advances the carousel without gating on "current question
  // answered". Submit (on the last question) additionally requires at least
  // one answer somewhere in the set — enforced by `rightEnabled` below.
  const goNext = useCallback(() => {
    if (disabled || blocked) return
    if (isLast) {
      // Guard against a stale hasAnyAnswer read during rapid clicks.
      const anyAnswered = set.questions.some((q) => {
        const v = answers[q.id]
        if (q.type === 'CHECKBOX' || q.type === 'ATTACHMENT_INVENTORY') return Array.isArray(v)
        return typeof v === 'string' && v.trim().length > 0
      })
      if (!anyAnswered || !isQuestionSetReady(set, answers)) return
      onSubmit()
      return
    }
    setSlideDir('left')
    setCurrentIndex((i) => Math.min(total - 1, i + 1))
  }, [disabled, blocked, isLast, onSubmit, total, set.questions, answers])

  const setAnswer = useCallback(
    (value: InterviewAnswers[string]) => {
      if (disabled || blocked) return
      const next: InterviewAnswers = { ...answers, [currentQuestion.id]: value }
      onAnswersChange(next)
      if (currentQuestion.type === 'RADIO' && !isLast) {
        if (advanceTimerRef.current != null) window.clearTimeout(advanceTimerRef.current)
        advanceTimerRef.current = window.setTimeout(() => {
          setSlideDir('left')
          setCurrentIndex((i) => Math.min(total - 1, i + 1))
        }, 180)
      }
    },
    [answers, currentQuestion, isLast, onAnswersChange, total, disabled, blocked],
  )

  // Submit (↵ on the last question) is blocked until the user has picked or
  // filled at least ONE answer — sending zero answers would be equivalent to
  // asking Line to guess with no signal, which the panel already handles via
  // the free-text escape hatch. Advancing between questions with `›` stays
  // free at all times.
  const hasAnyAnswer = set.questions.some((q) => {
    const v = answers[q.id]
    if (q.type === 'CHECKBOX') return Array.isArray(v) && v.length > 0
    if (q.type === 'ATTACHMENT_INVENTORY') return Array.isArray(v)
    return typeof v === 'string' && v.trim().length > 0
  })
  const answersReady = hasAnyAnswer && isQuestionSetReady(set, answers)
  const interactionBlocked = Boolean(disabled) || Boolean(blocked)
  const rightEnabled = !interactionBlocked && (!isLast || answersReady)
  const rightLabel = isLast
    ? (answersReady ? 'Submit answers' : 'Answer every required question to submit')
    : 'Next question'
  const rightGlyph = isLast ? <EnterGlyph /> : <ChevronRightGlyph />

  return (
    <div
      className={`interviewCarousel${blocked ? ' isBlocked' : ''}`}
      role="region"
      aria-label={set.title ?? 'Line asks'}
      aria-disabled={blocked ? 'true' : undefined}
    >
      <div className="interviewCarouselRow">
        {total >= 2 && (
        <Button
          size="lg"
          variant="tertiary"
          className={`interviewNav interviewNav--left${isFirst ? ' isDisabled' : ''}`}
          aria-label={`Previous question (${behindCount} behind)`}
          onClick={goPrev}
          disabled={isFirst || interactionBlocked}
        >
          <ChevronLeftGlyph />
          {behindCount > 0 && <span className="interviewNavCount">{behindCount}</span>}
        </Button>
        )}

        <div className={`interviewCarouselTrack slide-${slideDir}`} key={currentQuestion.id}>
          <div className="interviewCarouselPrompt">
            {currentQuestion.prompt}
            {currentQuestion.required && <em className="interviewRequiredMark" aria-hidden="true">*</em>}
            {total >= 2 && (
              <span className="interviewCarouselProgress">
                {t('NLSAiInterviewProgress', { current: currentIndex + 1, total })}
              </span>
            )}
          </div>
          <div className="interviewCarouselBody">
            {renderControl({ question: currentQuestion, answer, onChange: setAnswer, disabled: interactionBlocked })}
          </div>
        </div>

        <Button
          size="lg"
          variant={isLast ? 'primary' : 'secondary'}
          className={`interviewNav interviewNav--right${isLast ? ' isSubmit' : ''}`}
          aria-label={rightLabel}
          title={rightLabel}
          onClick={goNext}
          disabled={!rightEnabled}
        >
          {!isLast && aheadCount > 0 && <span className="interviewNavCount">{aheadCount}</span>}
          {rightGlyph}
        </Button>
      </div>
    </div>
  )
}

export default InterviewQuestionSet
